import React, { useCallback, useContext, useMemo, useState, Fragment, useEffect } from 'react';
import { LocalizationContext, useLocalizedStrings } from 'app/localization/Context';
import { DeviceType } from 'app/model/Device';
import { Split, TV } from '../RemoteControl';

import { ReactComponent as IconBack } from 'images/general/back.svg';
import { ReactComponent as IconLoading } from 'images/general/clone-of-connecting-white.svg';
import { ReactComponent as IconX } from 'images/general/x.svg';
import { ReactComponent as IconRefresh } from 'images/general/refresh.svg';
import { ReactComponent as IconTick } from 'images/general/tick.svg';

import { ReactComponent as IconRemoteAc } from 'images/device/symbol/hinava_gradient/remote-ac.svg';
// import { ReactComponent as IconRemoteFan } from 'images/device/symbol/hinava_gradient/remote-fan.svg';
// import { ReactComponent as IconRemoteHomeTheater } from 'images/device/symbol/hinava_gradient/remote-home-theater.svg';
// import { ReactComponent as IconRemoteProjector } from 'images/device/symbol/hinava_gradient/remote-projector.svg';
// import { ReactComponent as IconRemoteReciever } from 'images/device/symbol/hinava_gradient/remote-reciever.svg';
// import { ReactComponent as IconRemoteSoundbar } from 'images/device/symbol/hinava_gradient/remote-soundbar.svg';
import { ReactComponent as IconRemoteTv } from 'images/device/symbol/hinava_gradient/remote-tv.svg';


export function AddRemoteSelectDeviceType({ dismiss, onTypeSelected, supportedSubDeviceForDevice }) {
    const ar = useLocalizedStrings().addVirtualRemote;

    const supportedSubDevice = useMemo(() => {
        const supportedSubDeviceTypesForClient = [
            DeviceType.tvRemote,
            DeviceType.splitRemote,
            // DeviceType.receiverRemote,
            // DeviceType.fanRemote,
            // DeviceType.soundBarRemote,
            // DeviceType.projectorRemote,
            // DeviceType.homeTheatreRemote,
        ];

        if (supportedSubDeviceForDevice !== undefined) {
            return supportedSubDeviceTypesForClient.filter(
                value => supportedSubDeviceForDevice.includes(value));
        } else {
            return undefined; // loading
        }
    }, [supportedSubDeviceForDevice]);

    const deviceTypeInfo = useMemo(() => {
        let info = {};
        info[DeviceType.tvRemote] = {
            name: ar.subtypes.tv,
            icon: IconRemoteTv,
        }
        info[DeviceType.splitRemote] = {
            name: ar.subtypes.split,
            icon: IconRemoteAc,
        }
        return info;
    }, [ar]);

    return (
        <div className="add-remote-page-content">
            <IconBack className="nav-icon" />
            <div className="nav-button" onClick={dismiss} />

            <div className="title">{ar.selectSubType}</div>
            {
                (supportedSubDevice !== undefined) ? (
                    (supportedSubDevice.length > 0) ?
                        <div className="sub-device-types">
                            {supportedSubDevice.map((t, index) => {
                                var typeInfo = deviceTypeInfo[t];
                                return (
                                    <div key={"select_remote_type_" + String(t)}
                                        className="cell"
                                        onClick={() => {
                                            onTypeSelected(t);
                                        }}>
                                        {React.createElement(typeInfo.icon, { className: "icon" })}
                                        <div className="title">{typeInfo.name}</div>
                                        <IconBack className="next-icon" />
                                    </div>
                                );
                            })}
                            <div style={{
                                height: 16,
                            }} /> {/* blank space */}
                        </div>
                        : <div className="no-support">
                            <IconX className="icon" />
                            <div className="text">{ar.noRemoteSupported}</div>
                        </div>)
                    : <Loading text={ar.fetchingData} />
            }
        </div>
    );
}

function Loading({ text }) {
    return (
        <div className="loading">
            <IconLoading className="icon" />
            <div className="text">{text}</div>
        </div>
    )
}

export function AddRemoteSelectBrand({ dismiss, supportedBrands, onBrandSelected }) {
    const ar = useLocalizedStrings().addVirtualRemote;
    const [serachKey, setSerachKey] = useState("");
    const filteredSupportedBrands = useMemo(() => {
        if (supportedBrands !== undefined)
            return supportedBrands.filter(b => b["brand_name"].toLowerCase().includes(serachKey.toLowerCase()));
        else
            return undefined;
    }, [supportedBrands, serachKey]);

    return (
        <div className="add-remote-page-content">
            <IconBack className="nav-icon" />
            <div className="nav-button" onClick={dismiss} />

            <div className="title">{ar.selectBrand}</div>
            {
                (supportedBrands !== undefined) ?
                    <Fragment>
                        <input className="search-box" placeholder={ar.serachBrand}
                            value={serachKey} onChange={(e) => setSerachKey(e.target.value)} dir="ltr"></input>
                        {(filteredSupportedBrands.length > 0) ?
                            <div className="brands">
                                {filteredSupportedBrands.map((b, index) => {
                                    return (
                                        <div key={"select_remote_brand_" + String(index)}
                                            className="cell"
                                            onClick={() => {
                                                onBrandSelected(b["brand_id"]);
                                            }}>
                                            {b["brand_name"]}
                                        </div>
                                    );
                                })}
                            </div>
                            : <div className="no-support">
                                <IconX className="icon" />
                                <div className="text">{ar.noBrandFound}</div>
                            </div>}
                    </Fragment>
                    : <Loading text={ar.fetchingData} />
            }
        </div>
    );
}

export function AddRemoteTestCommand({
    dismiss, selectedSubType,
    testCommand, supportedIndices, onIndexSelected
}) {
    const l = useContext(LocalizationContext);
    const ar = l.strings.addVirtualRemote;

    const [indexOffset, setIndexOffset] = useState(0);

    const testCommandH = useCallback((func) => {
        return () => {
            testCommand(supportedIndices[indexOffset], func);
        }
    }, [testCommand, supportedIndices, indexOffset]);

    const tryNext = useCallback(() => {
        if (indexOffset < supportedIndices.length - 1) {
            setIndexOffset(indexOffset + 1);
        } else {
            setIndexOffset(0);
        }
    }, [indexOffset, supportedIndices]);

    const controllerStyle = useMemo(() => {
        return {
            transform: "rotateY(" + String(360 * indexOffset) + "deg)",
            transition: "transform 0.4s ease-in"
        }
    }, [indexOffset]);

    const deviceSpecificController = useMemo(() => {
        switch (selectedSubType) {
            case DeviceType.tvRemote:
                return <TV device={{ name: ar.subtypes.tv }} sendCommandH={testCommandH} style={controllerStyle} />
            // case DeviceType.receiverRemote:
            //     return null;
            // case DeviceType.fanRemote:
            //     return <Fan />
            case DeviceType.splitRemote:
                return <Split device={{ name: ar.subtypes.split }} sendCommandH={testCommandH} style={controllerStyle} />
            // case DeviceType.soundBarRemote:
            //     return <SoundBar />
            // case DeviceType.projectorRemote:
            //     return <Projector />
            // case DeviceType.homeTheatreRemote:
            //     return null;
            default:
                return null;
        }
    }, [controllerStyle, testCommandH, selectedSubType, ar]);

    return (
        <div className="add-remote-page-content">
            <IconBack className="nav-icon" />
            <div className="nav-button" onClick={dismiss} />

            {supportedIndices !== undefined ? <Fragment>
                {(supportedIndices.length > 0) ?
                    <Fragment>
                        {deviceSpecificController}
                        <div className="index-chooser">
                            <div className="hint">{ar.indexHint}</div>
                            <div className="select" onClick={() => { onIndexSelected(supportedIndices[indexOffset]) }}>
                                <div />
                                <div className="text">{ar.selectIndex}</div>
                                <IconTick className="icon" />
                                <div />
                            </div>
                            <div className="next" onClick={tryNext}>
                                <div />
                                <div className="text">{ar.nextIndex}</div>
                                <div className="progress">{
                                    l.localizeNumber(String(indexOffset + 1) + " / " + String(supportedIndices.length))
                                }</div>
                                <div />
                            </div>
                        </div>
                    </Fragment>
                    : <div className="no-support">
                        <IconX className="icon" />
                        <div className="text">{ar.noIndexFound}</div>
                    </div>}
            </Fragment> : <Loading text={ar.fetchingData} />}
        </div>
    );
}

export function AddRemoteCompleted({ cancel, newRemoteName, renameRemote }) {
    const ar = useLocalizedStrings().addVirtualRemote;

    const [name, setName] = useState("");
    const doneClick = useCallback(() => {
        if (name.length > 1) {
            renameRemote(name);
        }
    }, [name, renameRemote]);

    useEffect(() => {
        if (newRemoteName !== undefined) {
            setName(newRemoteName)
        }
    }, [newRemoteName]);

    return (
        <div className="add-remote-page-content">

            {newRemoteName !== undefined ?
                <Fragment>
                    <div className="title">{ar.enterName}</div>
                    <div className="info">{ar.deviceAddedInfo}</div>

                    <div className="device-name-label">{ar.name}</div>
                    <div className="input-container">
                        <input className="input"
                            placeholder={newRemoteName}
                            value={name}
                            onChange={(e) => { setName(e.target.value) }}></input>
                    </div>

                    {!(name.length > 1) ? <div className="error-label">{ar.youMustEnterAName}</div> : null}
                    <div className="bottom-blue-button" onClick={doneClick} style={{ opacity: (name.length > 1) ? 1 : 0.3 }}>
                        <div>{ar.setName}</div>
                        <IconTick className="icon" />
                    </div>
                </Fragment>
                : <Fragment>
                    <IconX className="nav-icon" />
                    <div className="nav-button" onClick={cancel} />
                    <Loading text={ar.addingRemote} />
                </Fragment>}
        </div>
    );
}

export function AddRemoteError({ onRetry, errorId, dismiss }) {
    const ar = useLocalizedStrings().addVirtualRemote;

    return (
        <div className="add-remote-page-content">
            <IconX className="nav-icon" /> <div className="nav-button" onClick={dismiss} />

            <div className="message-container">
                <div />
                <div className="message-content">
                    <IconX className="x-icon" />
                    <div className="label">{ar.errors[errorId]}</div>
                </div>
                <div />
            </div>

            <div className="bottom-blue-button" onClick={onRetry}>
                <div>{ar.retry}</div> <IconRefresh className="icon" />
            </div>
        </div>
    );
}